import { type PointerEvent as ReactPointerEvent, type ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { useIsBelowLg } from '../useMediaQuery'

// FlowEditor — the canvas where Skills (deterministic flows) are authored
// (design/components/flow-editor.html). Five node types (msg/ask/act/dec/fin)
// with a colour per type AND a text label (never colour alone). The two fin
// terminals differ by text + icon: ok (back to LLM) vs handoff (to a human).
// SkillValidator enforces invariant 22: every Skill needs fin.ok + fin.handoff,
// no orphans, every branch ends. Keyboard-operable (select/add via controls);
// pointer-drag (reposition a node, or drag from a node's connector to another
// to wire an edge) is a desktop enhancement layered on top — see onMoveNode /
// onConnect. Below lg the canvas is read-only (no palette, no drag).

export type FlowNodeType =
  | 'msg'
  | 'ask'
  | 'act'
  | 'dec'
  | 'answer'
  | 'route'
  | 'call'
  | 'handoff'
  | 'fin'

export interface FlowNodeData {
  id: string
  type: FlowNodeType
  position: { x: number; y: number }
  title: string
  detail?: string
  finKind?: 'ok' | 'handoff'
  /** The flow's entry node — gets an "Inicio" badge so it's obvious where every
   *  conversation begins. */
  isStart?: boolean
}

export interface FlowEdgeData {
  from: string
  to: string
  label?: string
}

export interface SkillValidationError {
  nodeId?: string
  rule: string
  message: string
}

export interface SkillValidation {
  valid: boolean
  errors: SkillValidationError[]
}

const NODE_W = 160
const NODE_H = 64

function MsgIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
    </svg>
  )
}
function AskIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 0 1 4.5 1.5c0 1.5-2 2-2 3M12 17h.01" />
    </svg>
  )
}
function ActIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M13 2 3 14h7l-1 8 10-12h-7Z" />
    </svg>
  )
}
function DecIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2 22 12 12 22 2 12Z" />
    </svg>
  )
}
function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}
function AnswerIcon() {
  // A spark / knowledge glyph: the bot answers from its knowledge base.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}
function RouteIcon() {
  // A branching/split glyph: discover what the user wants, then route.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <circle cx="18" cy="19" r="2" />
      <path d="M6 17V9a4 4 0 0 1 4-4h6M16 19h-2" />
    </svg>
  )
}
function HandoffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 3h5v5M21 3l-7 7M8 21H3v-5M3 21l7-7" />
    </svg>
  )
}
function CallIcon() {
  // A jump-out-and-return glyph: run another flow, then come back.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 7h9a4 4 0 0 1 4 4v2M21 7l-3-3M21 7l-3 3" />
      <path d="M20 17h-9a4 4 0 0 1-4-4M3 17l3-3M3 17l3 3" />
    </svg>
  )
}

interface NodeVisual {
  label: string
  accent: string
  box: string
  Icon: () => ReactNode
}

function nodeVisual(node: FlowNodeData): NodeVisual {
  if (node.type === 'fin') {
    return node.finKind === 'handoff'
      ? {
          label: 'fin · handoff',
          accent: 'text-sens',
          box: 'border-[rgba(214,69,63,0.45)] bg-sens-bg',
          Icon: HandoffIcon,
        }
      : {
          label: 'fin · ok',
          accent: 'text-ok',
          box: 'border-[rgba(0,179,132,0.45)] bg-ok-bg',
          Icon: CheckIcon,
        }
  }
  const map: Record<Exclude<FlowNodeType, 'fin'>, NodeVisual> = {
    msg: { label: 'msg', accent: 'text-link', box: 'border-line bg-surface', Icon: MsgIcon },
    ask: {
      label: 'ask',
      accent: 'text-accent-ink',
      box: 'border-line bg-surface',
      Icon: AskIcon,
    },
    act: { label: 'act', accent: 'text-info', box: 'border-line bg-surface', Icon: ActIcon },
    dec: { label: 'dec', accent: 'text-warn', box: 'border-line bg-surface', Icon: DecIcon },
    answer: {
      label: 'answer',
      accent: 'text-ok',
      box: 'border-line bg-surface',
      Icon: AnswerIcon,
    },
    route: {
      label: 'route',
      accent: 'text-info',
      box: 'border-line bg-surface',
      Icon: RouteIcon,
    },
    call: {
      label: 'call',
      accent: 'text-accent-ink',
      box: 'border-line bg-surface',
      Icon: CallIcon,
    },
    handoff: {
      label: 'handoff',
      accent: 'text-sens',
      box: 'border-line bg-surface',
      Icon: HandoffIcon,
    },
  }
  return map[node.type]
}

export interface FlowNodeProps {
  node: FlowNodeData
  selected?: boolean
  onClick?: () => void
  className?: string
}

/** One node on the Skill canvas: type badge (colour + text label, never colour
 *  alone), title, optional detail, and an "Inicio" badge on the entry node.
 *  Renders as a button so selection stays keyboard-operable. */
export function FlowNode({ node, selected, onClick, className }: FlowNodeProps) {
  const { t } = useTranslation('components')
  const v = nodeVisual(node)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`${t('ui.flow.nodePrefix')} ${v.label}: ${node.title}`}
      className={cn(
        'w-[160px] rounded-xl border p-[9px_11px] text-left shadow-card transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue',
        v.box,
        selected ? 'border-2 border-brand-blue' : 'hover:border-text-3',
        className,
      )}
    >
      {node.isStart && (
        <span className="mb-1 inline-block rounded-[4px] bg-brand-blue px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-[0.06em] text-white">
          Inicio
        </span>
      )}
      <span
        className={cn(
          'flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.06em] [&_svg]:size-[11px]',
          v.accent,
        )}
      >
        <v.Icon />
        {v.label}
      </span>
      <span className="mt-[3px] block text-[12px] font-bold leading-snug text-text">
        {node.title}
      </span>
      {node.detail && (
        <span className="mt-[3px] block text-[10px] italic leading-snug text-text-2">
          {node.detail}
        </span>
      )}
    </button>
  )
}

const paletteItems: { type: FlowNodeType; accent: string; Icon: () => ReactNode }[] = [
  { type: 'msg', accent: 'text-link', Icon: MsgIcon },
  { type: 'ask', accent: 'text-accent-ink', Icon: AskIcon },
  { type: 'route', accent: 'text-info', Icon: RouteIcon },
  { type: 'answer', accent: 'text-ok', Icon: AnswerIcon },
  { type: 'act', accent: 'text-info', Icon: ActIcon },
  { type: 'dec', accent: 'text-warn', Icon: DecIcon },
  // 'call' (sub-flow) is intentionally NOT addable: sub-flows aren't a real
  // feature yet (no way to author the called flow), so adding one would dangle.
  // Existing call nodes still render.
  { type: 'handoff', accent: 'text-sens', Icon: HandoffIcon },
  { type: 'fin', accent: 'text-ok', Icon: CheckIcon },
]

export interface NodePaletteProps {
  onAddNode?: ((type: FlowNodeType) => void) | undefined
}

/** Add-node toolbar above the canvas. `call` (sub-flow) is deliberately not
 *  addable — sub-flows aren't authorable yet; existing call nodes still
 *  render. */
export function NodePalette({ onAddNode }: NodePaletteProps) {
  const { t } = useTranslation('components')
  return (
    <div className="flex flex-wrap items-center gap-[7px] border-b border-line bg-surface px-3 py-2.5">
      <span className="mr-0.5 text-[10px] font-bold uppercase tracking-[0.05em] text-text-2">
        {t('ui.flow.addNode')}
      </span>
      {paletteItems.map((p) => (
        <button
          key={p.type}
          type="button"
          onClick={() => onAddNode?.(p.type)}
          className="flex items-center gap-1.5 rounded-lg border border-input-line bg-surface px-2.5 py-1.5 text-[11px] font-semibold text-text-2 transition-colors hover:bg-hover hover:text-text [&_svg]:size-[13px]"
        >
          <span className={p.accent}>
            <p.Icon />
          </span>
          {t(`ui.flow.types.${p.type}`)}
        </button>
      ))}
    </div>
  )
}

export interface SkillValidatorProps {
  validation: SkillValidation
}

/** Pill announcing the Skill's invariant-22 validation state: green when
 *  publishable, red with the problem count otherwise. role=status so screen
 *  readers hear the change. */
export function SkillValidator({ validation }: SkillValidatorProps) {
  const { t } = useTranslation('components')
  return (
    <span
      role="status"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-[11px] py-[5px] text-[11px] font-bold [&_svg]:size-[13px]',
        validation.valid ? 'bg-ok-bg text-ok' : 'bg-sens-bg text-sens',
      )}
    >
      {validation.valid ? <CheckIcon /> : <span aria-hidden>!</span>}
      {validation.valid
        ? t('ui.flow.valid')
        : t('ui.flow.problems', { count: validation.errors.length })}
    </span>
  )
}

export interface SkillPropertiesPanelProps {
  node?: FlowNodeData | undefined
  children?: ReactNode
}

export function SkillPropertiesPanel({ node, children }: SkillPropertiesPanelProps) {
  const { t } = useTranslation('components')
  return (
    <aside
      aria-label={t('ui.flow.properties')}
      className="w-[248px] flex-none overflow-y-auto border-l border-line bg-surface p-4"
    >
      <div className="text-[9.5px] font-bold uppercase tracking-[0.06em] text-text-2">
        {t('ui.flow.selectedNode')}
      </div>
      {node ? (
        <>
          <div className="mt-2 flex items-center gap-2 text-[13px] font-bold text-text">
            {node.title}
            <span className="rounded-[5px] bg-field px-1.5 py-0.5 text-[9px] font-bold uppercase text-text-2">
              {node.type === 'fin' ? `fin·${node.finKind ?? 'ok'}` : node.type}
            </span>
          </div>
          {children}
        </>
      ) : (
        <p className="mt-2 text-[11px] text-text-3">{t('ui.flow.selectHint')}</p>
      )}
    </aside>
  )
}

export interface FlowEditorProps {
  skillName: string
  intent?: string
  nodes: FlowNodeData[]
  edges: FlowEdgeData[]
  selectedId?: string
  onSelectNode?: (id: string) => void
  onAddNode?: (type: FlowNodeType) => void
  /** Drag-to-reposition committed (desktop only). Position is in canvas px,
   *  clamped to ≥ 0. When omitted, nodes are not draggable. */
  onMoveNode?: (id: string, position: { x: number; y: number }) => void
  /** Drag from a node's connector onto another node, committed on release
   *  (desktop only). The caller decides how to apply the edge (set the linear
   *  "next", add a branch, …). When omitted, the connector handles are hidden. */
  onConnect?: (from: string, to: string) => void
  validation: SkillValidation
  /** Properties content for the selected node. */
  renderProperties?: (node: FlowNodeData) => ReactNode
  /** Force the same read-only canvas the narrow viewport already produces: no
   *  add-node palette, no drag, no wiring. Nodes stay selectable, because
   *  reading the flow is the point. The caller decides WHY (see readOnlyNote). */
  readOnly?: boolean
  /** The notice shown in place of the palette when `readOnly`. Defaults to the
   *  narrow-viewport wording, which would be a lie for any other reason. */
  readOnlyNote?: string
  className?: string
}

/**
 * Canvas where a Skill (deterministic flow) is authored: palette, node graph,
 * properties panel, and the cannot-publish error list. Controlled — every
 * mutation flows through the callbacks. Pointer drag (reposition via
 * onMoveNode, wire edges via onConnect) is desktop-only; below lg the canvas
 * turns read-only.
 */
export function FlowEditor({
  skillName,
  intent,
  nodes,
  edges,
  selectedId,
  onSelectNode,
  onAddNode,
  onMoveNode,
  onConnect,
  validation,
  renderProperties,
  readOnly = false,
  readOnlyNote,
  className,
}: FlowEditorProps) {
  const { t } = useTranslation('components')
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const selected = selectedId ? byId.get(selectedId) : undefined
  // Node-graph editing needs the width: below lg the editor is read-only — the
  // add-node palette is replaced by a notice; nodes stay viewable + selectable
  // (full canvas keyboard nav is Phase 1.5). See tablet-responsive.md §6.5.
  const belowLg = useIsBelowLg()

  // Pointer-drag (desktop only). `drag` repositions a node — tracked locally so
  // the node follows the cursor and the move is committed once on release;
  // `connect` rubber-bands a new edge from a node's connector to a target node.
  // Both are pure pointer enhancements; selection + edge editing stay
  // keyboard-operable via the node buttons and the properties panel.
  const draggable = !belowLg && !readOnly && !!onMoveNode
  const connectable = !belowLg && !readOnly && !!onConnect
  const canvasRef = useRef<HTMLDivElement>(null)
  const suppressClick = useRef(false)
  const [drag, setDrag] = useState<{
    id: string
    ox: number
    oy: number
    sx: number
    sy: number
    cx: number
    cy: number
    moved: boolean
  } | null>(null)
  const [connect, setConnect] = useState<{
    from: string
    x: number
    y: number
    over: string | null
  } | null>(null)

  /** Live position of a node — the in-flight drag position while it's being
   *  dragged, otherwise its committed position. */
  function posOf(id: string): { x: number; y: number } | null {
    const n = byId.get(id)
    if (!n) return null
    if (drag && drag.id === id) {
      return {
        x: Math.max(0, drag.ox + (drag.cx - drag.sx)),
        y: Math.max(0, drag.oy + (drag.cy - drag.sy)),
      }
    }
    return n.position
  }
  /** Client coords → canvas coords (the inner positioned div already accounts
   *  for scroll via getBoundingClientRect). */
  function toCanvas(clientX: number, clientY: number): { x: number; y: number } {
    const r = canvasRef.current?.getBoundingClientRect()
    return { x: clientX - (r?.left ?? 0), y: clientY - (r?.top ?? 0) }
  }
  function nodeAt(x: number, y: number): string | null {
    for (let i = nodes.length - 1; i >= 0; i--) {
      const n = nodes[i]!
      const p = posOf(n.id)!
      if (x >= p.x && x <= p.x + NODE_W && y >= p.y && y <= p.y + NODE_H) return n.id
    }
    return null
  }

  function onNodeDown(e: ReactPointerEvent, node: FlowNodeData): void {
    if (!draggable || e.button !== 0) return
    suppressClick.current = false
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    setDrag({
      id: node.id,
      ox: node.position.x,
      oy: node.position.y,
      sx: e.clientX,
      sy: e.clientY,
      cx: e.clientX,
      cy: e.clientY,
      moved: false,
    })
  }
  function onNodeMove(e: ReactPointerEvent): void {
    if (!drag) return
    const moved =
      drag.moved || Math.abs(e.clientX - drag.sx) > 3 || Math.abs(e.clientY - drag.sy) > 3
    if (moved) suppressClick.current = true
    setDrag({ ...drag, cx: e.clientX, cy: e.clientY, moved })
  }
  function onNodeUp(): void {
    if (!drag) return
    if (drag.moved) {
      const p = posOf(drag.id)
      if (p) onMoveNode?.(drag.id, { x: Math.round(p.x), y: Math.round(p.y) })
    } else {
      // A press without movement is a tap = selection. We can't rely on the
      // FlowNode's onClick here: onNodeDown took a pointer capture (so drags
      // stay smooth when the cursor leaves the node), and the capture routes
      // the synthetic click to this wrapper instead of the inner button, so
      // its onClick never fires. Select deterministically on pointerup.
      onSelectNode?.(drag.id)
    }
    setDrag(null)
  }

  function onConnDown(e: ReactPointerEvent, node: FlowNodeData): void {
    e.stopPropagation() // don't also start a node reposition
    if (!connectable || e.button !== 0) return
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    const p = toCanvas(e.clientX, e.clientY)
    setConnect({ from: node.id, x: p.x, y: p.y, over: null })
  }
  function onConnMove(e: ReactPointerEvent): void {
    if (!connect) return
    const p = toCanvas(e.clientX, e.clientY)
    const hit = nodeAt(p.x, p.y)
    setConnect({ ...connect, x: p.x, y: p.y, over: hit && hit !== connect.from ? hit : null })
  }
  function onConnUp(e: ReactPointerEvent): void {
    if (!connect) return
    const p = toCanvas(e.clientX, e.clientY)
    const target = nodeAt(p.x, p.y)
    if (target && target !== connect.from) onConnect?.(connect.from, target)
    setConnect(null)
  }

  const canvasW = Math.max(540, ...nodes.map((n) => (posOf(n.id)?.x ?? 0) + NODE_W + 40))
  const canvasH = Math.max(460, ...nodes.map((n) => (posOf(n.id)?.y ?? 0) + NODE_H + 40))

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="mb-3.5 flex items-center gap-3">
        <span className="flex items-center gap-2 text-[15px] font-bold text-text">
          {skillName}
          {intent && (
            <span className="rounded-[5px] bg-[color-mix(in_srgb,var(--accent-ink)_10%,transparent)] px-2 py-0.5 font-mono text-[10px] font-bold tracking-[0.02em] text-accent-ink">
              intent: {intent}
            </span>
          )}
        </span>
        <span className="ml-auto">
          <SkillValidator validation={validation} />
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-line bg-surface">
        {belowLg || readOnly ? (
          <div
            role="note"
            className="flex items-center gap-2 border-b border-line bg-surface-2 px-3 py-2.5 text-[11px] font-semibold text-text-2"
          >
            {/* El aviso de pantalla angosta manda cuando ES la pantalla: decir
                "lo escribe tu configuración" en un celular sería falso. */}
            {belowLg ? t('ui.flow.readOnly') : (readOnlyNote ?? t('ui.flow.readOnly'))}
          </div>
        ) : (
          <NodePalette onAddNode={onAddNode} />
        )}
        <div className="flex min-h-[460px]">
          <div
            className="relative flex-1 overflow-auto bg-surface-2"
            style={{
              backgroundImage: 'radial-gradient(var(--line) 1px, transparent 1px)',
              backgroundSize: '18px 18px',
            }}
          >
            <div
              ref={canvasRef}
              className="relative mx-auto"
              style={{ width: canvasW, height: canvasH }}
            >
              <svg
                aria-hidden
                width={canvasW}
                height={canvasH}
                className="pointer-events-none absolute left-0 top-0"
              >
                {edges.map((e, i) => {
                  const from = posOf(e.from)
                  const to = posOf(e.to)
                  if (!from || !to) return null
                  const bx = from.x + NODE_W / 2
                  const by = from.y + NODE_H
                  const tx = to.x + NODE_W / 2
                  const ty = to.y
                  const mx = (bx + tx) / 2
                  const my = (by + ty) / 2
                  return (
                    <g key={i}>
                      <path
                        d={`M${bx},${by} C${bx},${by + 26} ${tx},${ty - 26} ${tx},${ty}`}
                        stroke="var(--text-3)"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      {e.label && (
                        <>
                          <rect
                            x={mx - 13}
                            y={my - 8}
                            width="26"
                            height="16"
                            rx="4"
                            fill="var(--surface)"
                            stroke="var(--line)"
                          />
                          <text
                            x={mx}
                            y={my + 3}
                            textAnchor="middle"
                            className="fill-text-2 text-[10px] font-bold"
                          >
                            {e.label}
                          </text>
                        </>
                      )}
                    </g>
                  )
                })}
                {/* Rubber-band line while wiring a new edge. */}
                {connect &&
                  (() => {
                    const f = posOf(connect.from)
                    if (!f) return null
                    return (
                      <path
                        d={`M${f.x + NODE_W / 2},${f.y + NODE_H} L${connect.x},${connect.y}`}
                        stroke="var(--brand-blue)"
                        strokeWidth="2"
                        strokeDasharray="4 3"
                        fill="none"
                      />
                    )
                  })()}
              </svg>
              {/* The connector <svg> above is aria-hidden; the nodes carry the
                  semantics. A labelled list region gives screen-reader users a
                  read-only listing of the flow's nodes (in source order) even
                  though they're visually positioned on the canvas. */}
              <div role="list" aria-label={t('ui.flow.nodeList')}>
                {nodes.map((n) => {
                  const p = posOf(n.id)!
                  return (
                    <div
                      key={n.id}
                      role="listitem"
                      className={cn('group absolute', draggable && 'touch-none')}
                      style={{ left: p.x, top: p.y }}
                      {...(draggable
                        ? {
                            onPointerDown: (e: ReactPointerEvent) => onNodeDown(e, n),
                            onPointerMove: onNodeMove,
                            onPointerUp: onNodeUp,
                          }
                        : {})}
                    >
                      <FlowNode
                        node={n}
                        selected={n.id === selectedId}
                        onClick={() => {
                          if (suppressClick.current) return
                          onSelectNode?.(n.id)
                        }}
                        className={cn(
                          draggable && 'cursor-grab active:cursor-grabbing',
                          connect?.over === n.id && 'ring-2 ring-brand-blue ring-offset-1',
                        )}
                      />
                      {connectable && (
                        <span
                          aria-hidden
                          title={t('ui.flow.connectHint')}
                          onPointerDown={(e: ReactPointerEvent) => onConnDown(e, n)}
                          onPointerMove={onConnMove}
                          onPointerUp={onConnUp}
                          className={cn(
                            'absolute -bottom-[7px] left-1/2 size-3.5 -translate-x-1/2 cursor-crosshair touch-none rounded-full border-2 border-surface bg-text-3 transition-all hover:scale-110 hover:bg-brand-blue',
                            // Faintly visible by default so the connect affordance
                            // is discoverable; full on hover / selection.
                            'opacity-40 group-hover:opacity-100',
                            (n.id === selectedId || connect?.from === n.id) && 'opacity-100',
                          )}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <SkillPropertiesPanel node={selected}>
            {selected && renderProperties?.(selected)}
          </SkillPropertiesPanel>
        </div>
      </div>

      {!validation.valid && validation.errors.length > 0 && (
        <div
          role="alert"
          className="mt-3 rounded-lg border border-[color-mix(in_srgb,var(--sens)_30%,transparent)] bg-sens-bg p-3"
        >
          <div className="text-[11px] font-bold text-sens">{t('ui.flow.cannotPublish')}</div>
          <ul className="mt-1.5 flex flex-col gap-1">
            {validation.errors.map((err, i) => (
              <li key={i} className="text-[11px] text-text-2">
                <span className="font-mono text-[10px] font-bold text-sens">{err.rule}</span>:{' '}
                {err.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
